import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Plus, X } from "lucide-react";
import { useFocusNewRow, enterAddsRow } from "./useFocusNewRow";
import { MoveButtons, altArrowMove, useRowDrag, DragHandle } from "./MoveButtons";

const firstField = (i) => `[data-testid="bom-comp-item-${i}"]`;
const qtyField = (i) => `[data-testid="bom-comp-qty-${i}"]`;

export function BomComponentsEditor({ components, options, onAdd, onChange, onRemove, onMove }) {
  const focus = useFocusNewRow(components.length, firstField);
  const add = () => { focus.arm(); onAdd(); };
  const drag = useRowDrag(onMove);
  const count = components.length;
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3" ref={focus.ref}>
      <div className="flex items-center justify-between gap-2">
        <Label>Recipe Components (per Unit)</Label>
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={add} data-testid="bom-add-component">
          <Plus className="h-3 w-3 mr-1" /> Component
        </Button>
      </div>
      {count === 0 && <p className="text-[11px] text-slate-400 italic">No components yet — add raw materials or other manufactured parts (nested recipes supported).</p>}
      {components.map((c, i) => (
        <div key={c._key || c.item_id || i} data-testid={`bom-comp-row-${i}`} {...drag.row(i)} className={`flex items-center gap-2 ${drag.rowClass(i)}`}
          onKeyDown={altArrowMove(i, count, onMove, focus.ref, (to) => `${firstField(to)}, ${qtyField(to)}`)}>
          <DragHandle {...drag.handle(i)} testid={`bom-comp-drag-${i}`} />
          <MoveButtons i={i} count={count} onMove={onMove} prefix="bom-comp" />
          <span className="text-[10px] font-mono-data text-slate-400 w-4 text-right">{i + 1}</span>
          <SearchableSelect
            value={c.item_id}
            onValueChange={(v) => onChange(i, "item_id", v)}
            placeholder="Pick item…"
            searchPlaceholder="Search items…"
            emptyText="No items found."
            className="flex-1"
            triggerClassName="h-9 text-xs"
            options={options}
            testid={`bom-comp-item-${i}`}
          />
          <Input type="number" min="0.001" step="0.001" value={c.quantity_per_unit}
            onChange={(e) => onChange(i, "quantity_per_unit", e.target.value)}
            onKeyDown={enterAddsRow(i === count - 1, add)}
            className="w-24 h-9 text-xs" data-testid={`bom-comp-qty-${i}`} />
          <button type="button" onClick={() => onRemove(i)} className="text-slate-300 hover:text-red-500" data-testid={`bom-comp-remove-${i}`}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      {count > 1 && <p className="text-[10px] text-slate-400">Enter on the last row adds a component · use ▲▼, Alt+↑/↓ or drag ⋮⋮ to put materials in build order.</p>}
    </div>
  );
}
